import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import { Modal, ModalBody } from "reactstrap";
import {
  FaHeart,
  FaSearch,
  FaBed,
  FaBath,
  FaShareAlt,
  FaTrashAlt,
  FaCompass,
  FaPhoneAlt,
  FaBuilding,
  FaEye,
  FaCheckCircle,
} from "react-icons/fa";
import { IoLocationSharp, IoSparkles, IoShieldCheckmark } from "react-icons/io5";
import { BiChevronRight } from "react-icons/bi";
import {
  getContactedProperties,
  removeContactedProperty,
  clearContactedProperties,
  SAMPLE_CONTACTED_PROPERTIES,
  getViewedCount,
} from "../../utils/activityUtils";
import {
  getShortlistedProperties,
  toggleShortlistProperty,
  isPropertyShortlisted,
} from "../../utils/shortlistUtils";
import PropertyDetailsModel from "../home/latestProperty/PropertyDetailsModel";
import "./ActivityPages.css";

const Contacted = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuth, loginData } = useSelector((store) => store.Athentication);

  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState("recent");
  const [selectedPropForContact, setSelectedPropForContact] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [actionType, setActionType] = useState("contact");
  const [shortlistVersion, setShortlistVersion] = useState(0);

  // Load contacted properties
  useEffect(() => {
    const list = getContactedProperties();
    setItems(list);
  }, []);

  const shortlistCount = useMemo(() => {
    return getShortlistedProperties().length;
  }, [shortlistVersion]);

  const viewedCount = useMemo(() => {
    return getViewedCount();
  }, []);

  // Handle heart toggle
  const handleToggleShortlist = (e, prop) => {
    e.preventDefault();
    e.stopPropagation();
    const added = toggleShortlistProperty(prop);
    setShortlistVersion((v) => v + 1);
    if (added) {
      toast.success("Saved to Shortlist!", { position: "top-center", autoClose: 1500 });
    } else {
      toast.info("Removed from Shortlist", { position: "top-center", autoClose: 1500 });
    }
  };

  // Handle remove
  const handleRemove = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const updated = removeContactedProperty(id);
    setItems(updated);
    toast.info("Removed from contacted list", { position: "top-center", autoClose: 1400 });
  };

  // Handle clear all
  const handleClearAll = () => {
    if (window.confirm("Are you sure you want to clear your contacted properties history?")) {
      clearContactedProperties();
      setItems([]);
      toast.success("Contacted history cleared", { position: "top-center", autoClose: 1500 });
    }
  };

  // Load sample properties
  const handleLoadSample = () => {
    setItems(SAMPLE_CONTACTED_PROPERTIES);
    try {
      localStorage.setItem("mgh_contacted_properties", JSON.stringify(SAMPLE_CONTACTED_PROPERTIES));
    } catch (e) {}
    toast.success("Sample contacted properties loaded!", { position: "top-center", autoClose: 1500 });
  };

  // Open contact modal
  const handleOpenContact = (e, prop, type = "phone") => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedPropForContact(prop);
    setActionType(type);
    setContactModalOpen(true);
  };

  // Handle share
  const handleShare = async (e, prop) => {
    e.preventDefault();
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/property-info/${encodeURIComponent(
      (prop.apartment || prop.title || "property").toLowerCase().replace(/\s+/g, "-")
    )}/${prop._id || prop.propertyID}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: prop.apartment || "Property on EW Realty",
          text: `Check out ${prop.apartment || "this property"} on EW Realty`,
          url: shareUrl,
        });
        return;
      } catch (err) {}
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard!", { position: "top-center", autoClose: 1600 });
    } catch (err) {
      toast.info(`Share link: ${shareUrl}`, { position: "top-center", autoClose: 2500 });
    }
  };

  // Navigate to property details
  const handleCardClick = (prop) => {
    const slug = encodeURIComponent(
      (prop.apartment || prop.title || "property").toLowerCase().replace(/\s+/g, "-")
    );
    navigate(`/property-info/${slug}/${prop._id || prop.propertyID}`);
  };

  // Filter & Sort
  const filteredItems = useMemo(() => {
    let result = [...items];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          (p.apartment && p.apartment.toLowerCase().includes(q)) ||
          (p.locality && p.locality.toLowerCase().includes(q)) ||
          (p.city && p.city.toLowerCase().includes(q)) ||
          (p.postUserName && p.postUserName.toLowerCase().includes(q)) ||
          (p.propertyID && p.propertyID.toLowerCase().includes(q))
      );
    }

    if (selectedCategory !== "All") {
      result = result.filter((p) => {
        const cat = (p.category || "").toLowerCase();
        const sub = (p.subCategory || "").toLowerCase();
        const sup = (p.superCategory || "").toLowerCase();

        if (selectedCategory === "Apartments") return sub.includes("apartment") || sub.includes("flat");
        if (selectedCategory === "Villas") return sub.includes("villa") || sub.includes("house");
        if (selectedCategory === "Rent") return cat === "rent" || cat === "lease";
        if (selectedCategory === "Commercial") return sup === "commercial" || sub.includes("commercial");
        return true;
      });
    }

    if (sortBy === "price_low") {
      result.sort((a, b) => Number(a.expectedPrice || 0) - Number(b.expectedPrice || 0));
    } else if (sortBy === "price_high") {
      result.sort((a, b) => Number(b.expectedPrice || 0) - Number(a.expectedPrice || 0));
    }

    return result;
  }, [items, searchQuery, selectedCategory, sortBy]);

  return (
    <div className="activity-page">
      <Helmet>
        <title>Contacted Properties | EW Realty</title>
        <meta
          name="description"
          content="View properties and advertisers you have contacted or requested phone numbers from."
        />
      </Helmet>

      <div className="activity-container">
        {/* Breadcrumb */}
        <div className="activity-breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <span className="current">Contacted Properties</span>
        </div>

        {/* Activity Navigation Tabs */}
        <div className="activity-tabs-nav">
          <Link to="/recentelysearched" className="activity-tab-item">
            <FaCompass />
            <span>Recent Searches</span>
          </Link>
          <Link to="/viewed" className="activity-tab-item">
            <FaEye />
            <span>Recently Viewed</span>
            <span className="activity-tab-badge">{viewedCount}</span>
          </Link>
          <Link to="/shortlisted" className="activity-tab-item">
            <FaHeart />
            <span>Shortlisted</span>
            <span className="activity-tab-badge">{shortlistCount}</span>
          </Link>
          <div className="activity-tab-item active">
            <FaPhoneAlt />
            <span>Contacted</span>
            <span className="activity-tab-badge">{items.length}</span>
          </div>
        </div>

        {/* Header Summary Card */}
        <div className="activity-header-card">
          <div className="activity-header-left">
            <div className="activity-header-icon">
              <FaPhoneAlt />
            </div>
            <div>
              <h1 className="activity-header-title">Contacted Properties</h1>
              <p className="activity-header-subtitle">
                Track advertiser connections, phone numbers unlocked, and callback requests.
              </p>
            </div>
          </div>
          <div className="activity-header-actions">
            {items.length > 0 && (
              <button type="button" className="activity-clear-btn" onClick={handleClearAll}>
                <FaTrashAlt />
                <span>Clear History</span>
              </button>
            )}
            <Link to="/allproperty" className="activity-explore-btn">
              <span>Find More</span>
              <BiChevronRight size={18} />
            </Link>
          </div>
        </div>

        {/* Toolbar */}
        {items.length > 0 && (
          <div className="activity-toolbar">
            <div className="activity-search-box">
              <FaSearch />
              <input
                type="text"
                className="activity-search-input"
                placeholder="Search contacted by name, project, location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="activity-filter-pills">
              {["All", "Apartments", "Villas", "Rent", "Commercial"].map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={`activity-pill ${selectedCategory === cat ? "active" : ""}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>

            <select
              className="activity-sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="recent">Recently Contacted</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
            </select>
          </div>
        )}

        {/* Properties Grid */}
        {filteredItems.length > 0 ? (
          <div className="activity-grid">
            {filteredItems.map((prop, idx) => {
              const imageSrc =
                (prop.DeskImg && prop.DeskImg[0]) ||
                (prop.photos && prop.photos[0]) ||
                "/assets/images/hero_banner_new.png";
              const isFav = isPropertyShortlisted(prop);

              return (
                <article
                  key={prop._id || prop.propertyID || idx}
                  className="activity-card"
                  onClick={() => handleCardClick(prop)}
                >
                  {/* Media */}
                  <div className="activity-card-media">
                    <img
                      src={imageSrc}
                      alt={prop.apartment || prop.title || "Property"}
                      className="activity-card-img"
                      loading="lazy"
                    />

                    <div className="activity-badge-verified">
                      <IoShieldCheckmark />
                      <span>VERIFIED</span>
                    </div>

                    <div className="activity-badge-category">
                      {prop.category || "FOR SALE"}
                    </div>

                    <div className="activity-card-status-badge">
                      <FaCheckCircle style={{ marginRight: "3px" }} />
                      {prop.contactStatus || "Contacted"}
                    </div>

                    <button
                      type="button"
                      className={`activity-card-fav-btn ${isFav ? "active" : ""}`}
                      onClick={(e) => handleToggleShortlist(e, prop)}
                      title={isFav ? "Remove from shortlist" : "Add to shortlist"}
                    >
                      <FaHeart />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="activity-card-body">
                    <div className="activity-card-price-row">
                      <span className="activity-card-price">
                        ₹ {prop.visiblePrice || "On Request"}
                      </span>
                      {prop.noofBedrooms && (
                        <span className="activity-card-bhk">
                          {prop.noofBedrooms} BHK
                        </span>
                      )}
                    </div>

                    <h3 className="activity-card-title">
                      {prop.apartment || prop.title || "Premium Property"}
                    </h3>

                    <div className="activity-card-location">
                      <IoLocationSharp />
                      <span>{prop.locality || "Prime Locality"}, {prop.city || "Hyderabad"}</span>
                    </div>

                    {/* Specs Row */}
                    <div className="activity-specs-row">
                      <div className="activity-spec-item" title="Carpet Area">
                        <FaBuilding />
                        <span>{prop.carpetArea ? `${prop.carpetArea} sq.ft` : "Spacious"}</span>
                      </div>
                      <span className="activity-spec-dot">•</span>
                      <div className="activity-spec-item" title="Bedrooms">
                        <FaBed />
                        <span>{prop.noofBedrooms ? `${prop.noofBedrooms} Beds` : "3 Beds"}</span>
                      </div>
                      <span className="activity-spec-dot hide-on-mobile">•</span>
                      <div className="activity-spec-item hide-on-mobile" title="Bathrooms">
                        <FaBath />
                        <span>{prop.noofBathrooms ? `${prop.noofBathrooms} Baths` : "3 Baths"}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="activity-card-actions">
                      <button
                        type="button"
                        className="activity-view-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardClick(prop);
                        }}
                      >
                        <span>View</span>
                      </button>

                      <button
                        type="button"
                        className="activity-contact-btn"
                        onClick={(e) => handleOpenContact(e, prop, "phone")}
                        title="Call Advertiser Again"
                      >
                        <FaPhoneAlt />
                        <span className="btn-text-full">Call</span>
                      </button>

                      <button
                        type="button"
                        className="activity-share-btn"
                        onClick={(e) => handleShare(e, prop)}
                        title="Share Property"
                      >
                        <FaShareAlt />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          /* Empty State */
          <div className="activity-empty-card">
            <div className="activity-empty-graphic">
              <FaPhoneAlt />
            </div>
            <h2 className="activity-empty-title">You Haven't Contacted Anyone Lately</h2>
            <p className="activity-empty-desc">
              When you view phone numbers, call dealers, or message property owners on EW Realty, all your contacted properties will be organized here.
            </p>
            <div className="activity-empty-actions">
              <Link to="/allproperty" className="activity-explore-btn">
                <FaCompass />
                <span>Explore Properties</span>
              </Link>
              <button
                type="button"
                className="activity-sample-btn"
                onClick={handleLoadSample}
              >
                <IoSparkles />
                <span>Show Sample Contacted</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Details & Contact Modal (Centered Dead-Center) */}
      {contactModalOpen && selectedPropForContact && (
        <Modal
          isOpen={contactModalOpen}
          toggle={() => setContactModalOpen(!contactModalOpen)}
          className="modalmainnew"
          centered
        >
          <div className="model-wrappernew">
            <ModalBody style={{ padding: 0 }}>
              <PropertyDetailsModel
                closePropdet={() => setContactModalOpen(false)}
                propertyprop={selectedPropForContact}
                actionType={actionType}
                changeView={() => {}}
              />
            </ModalBody>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Contacted;